/**
 * In-App Purchase service using RevenueCat.
 * Only active on iOS — Android/Web uses Stripe/Vipps.
 */
import { Capacitor } from '@capacitor/core';
import { Purchases, LOG_LEVEL } from '@revenuecat/purchases-capacitor';

const REVENUECAT_APPLE_API_KEY = 'appl_UiihVabWdiAYsMLruCHAbzsNUoI';
const ENTITLEMENT_ID = 'Fricamp Pro';
const PREMIUM_ENTITLEMENT_ID = 'Fricamp Premium';

let isInitialized = false;
let initPromise: Promise<void> | null = null;

/**
 * Initialize RevenueCat with the user's Firebase UID.
 * Only runs on iOS — no-op on other platforms.
 */
export const initializeIAP = async (userId: string): Promise<void> => {
  if (Capacitor.getPlatform() !== 'ios') return;
  if (isInitialized) return;
  if (initPromise) return initPromise;

  initPromise = (async () => {
    try {
      await Purchases.setLogLevel({ level: LOG_LEVEL.WARN });
      await Purchases.configure({
        apiKey: REVENUECAT_APPLE_API_KEY,
        appUserID: userId,
      });
      isInitialized = true;
    } catch (error) {
      console.error('RevenueCat init error:', error);
    }
  })();
  return initPromise;
};

/**
 * Ensure RevenueCat's app_user_id == the Firebase UID.
 *
 * Configuring alone is not enough: if the SDK was ever configured/used before
 * auth resolved, purchases land on an anonymous `$RCAnonymousID:...` id and the
 * webhook then creates a phantom user doc. logIn() aliases that anonymous id to
 * the Firebase UID in RevenueCat, so the purchase (and webhook) attach to the
 * real account. Safe to call repeatedly. Family-shared subs stay 0-revenue
 * regardless — this only fixes identity, not who pays.
 */
export const identifyIAP = async (userId: string): Promise<void> => {
  if (Capacitor.getPlatform() !== 'ios' || !userId) return;
  await initializeIAP(userId);
  if (!isInitialized) return;
  try {
    await Purchases.logIn({ appUserID: userId });
  } catch (error) {
    console.error('RevenueCat identify error:', error);
  }
};

/**
 * Log in a different user (e.g. after auth state change).
 */
export const loginIAP = async (userId: string): Promise<void> => {
  if (Capacitor.getPlatform() !== 'ios' || !isInitialized) return;
  try {
    await Purchases.logIn({ appUserID: userId });
  } catch (error) {
    console.error('RevenueCat login error:', error);
  }
};

/**
 * Log out IAP user (on sign-out).
 */
export const logoutIAP = async (): Promise<void> => {
  if (Capacitor.getPlatform() !== 'ios' || !isInitialized) return;
  try {
    await Purchases.logOut();
  } catch (error) {
    console.error('RevenueCat logout error:', error);
  }
};

/**
 * Get the current monthly offering package (Supporter level).
 * Returns null if not on iOS or no offering available.
 */
export const getMonthlyPackage = async (): Promise<any | null> => {
  if (Capacitor.getPlatform() !== 'ios') return null;
  if (!isInitialized && initPromise) await initPromise;
  if (!isInitialized) return null;

  try {
    const offerings = await Purchases.getOfferings();
    // Try current offering first, then fall back to 'fricamp' offering by name
    const offering = offerings.current ?? offerings.all?.['fricamp'] ?? null;
    const pkg = offering?.monthly ?? offering?.availablePackages?.[0] ?? null;
    return pkg;
  } catch (error: any) {
    console.error('RevenueCat getOfferings error:', error);
    return null;
  }
};

/**
 * Get the Premium offering package.
 * Returns null if not on iOS or no premium offering available.
 */
export const getPremiumPackage = async (): Promise<any | null> => {
  if (Capacitor.getPlatform() !== 'ios') return null;
  if (!isInitialized && initPromise) await initPromise;
  if (!isInitialized) return null;

  try {
    const offerings = await Purchases.getOfferings();
    const offering = offerings.all?.['fricamp_premium'] ?? null;
    const pkg = offering?.monthly ?? offering?.availablePackages?.[0] ?? null;
    return pkg;
  } catch (error: any) {
    console.error('RevenueCat getPremiumPackage error:', error);
    return null;
  }
};

/**
 * Purchase the given package. Returns updated CustomerInfo.
 */
export const purchasePackage = async (pkg: any): Promise<any> => {
  const { customerInfo } = await Purchases.purchasePackage({
    aPackage: pkg,
  });
  return customerInfo;
};

/**
 * Restore previous purchases. Returns updated CustomerInfo.
 */
export const restorePurchases = async (): Promise<any> => {
  const { customerInfo } = await Purchases.restorePurchases();
  return customerInfo;
};

/**
 * Check if the user has an active 'Fricamp Pro' entitlement (Supporter level).
 */
export const hasActiveEntitlement = (info: any): boolean => {
  return info?.entitlements?.active?.[ENTITLEMENT_ID] !== undefined ||
    info?.entitlements?.active?.[PREMIUM_ENTITLEMENT_ID] !== undefined;
};

/**
 * Check if the user has an active Premium entitlement specifically.
 */
export const hasActivePremiumEntitlement = (info: any): boolean => {
  return info?.entitlements?.active?.[PREMIUM_ENTITLEMENT_ID] !== undefined;
};

/**
 * Get current customer info.
 */
export const getCustomerInfo = async (): Promise<any | null> => {
  if (Capacitor.getPlatform() !== 'ios' || !isInitialized) return null;
  try {
    const { customerInfo } = await Purchases.getCustomerInfo();
    return customerInfo;
  } catch (error) {
    console.error('RevenueCat getCustomerInfo error:', error);
    return null;
  }
};

/**
 * Check if IAP is available on this platform.
 */
export const isIAPPlatform = (): boolean => {
  return Capacitor.getPlatform() === 'ios';
};
